/**
 * Re-usable attachment picker used inside the procurement dialogs.
 *
 * - Uploads each selected file immediately to
 *   POST /api/projects/{projectId}/procurement/attachments
 * - Stores the returned metadata in the parent's `attachments` form array
 * - Lets the user remove a file (DELETE on the server, splice from the array)
 * - Read-only when `mode === 'view'`
 *
 * The parent dialog then includes `attachments` in the entity payload it
 * sends on save; the metadata is persisted with the vendor / PO / contract.
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { BACKEND_URL } from '../config';
import { Button } from './ui/button';
import { Paperclip, Trash2, Loader2, Download, FileText, FileSpreadsheet, FileImage, File as FileIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${BACKEND_URL}/api`;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const iconFor = (filename = '', contentType = '') => {
  const f = filename.toLowerCase();
  const t = (contentType || '').toLowerCase();
  if (t.startsWith('image/') || /\.(png|jpg|jpeg|gif|webp|svg)$/.test(f)) return <FileImage className="h-4 w-4 text-violet-600" />;
  if (/\.(xls|xlsx|csv|ods)$/.test(f) || t.includes('spreadsheet')) return <FileSpreadsheet className="h-4 w-4 text-emerald-700" />;
  if (/\.(pdf|doc|docx|txt|md)$/.test(f) || t.includes('pdf') || t.includes('word')) return <FileText className="h-4 w-4 text-blue-600" />;
  return <FileIcon className="h-4 w-4 text-gray-500" />;
};

const formatSize = (bytes = 0) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const AttachmentsPicker = ({ mode = 'edit', projectId, value, onChange }) => {
  const items = Array.isArray(value) ? value : [];
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const isView = mode === 'view';

  const handleFiles = async (filesList) => {
    if (!projectId) {
      toast.error('Project context is missing — cannot upload yet');
      return;
    }
    const files = Array.from(filesList || []);
    if (files.length === 0) return;
    setUploading(true);
    const next = [...items];
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append('file', f);
        const res = await axios.post(
          `${API}/projects/${projectId}/procurement/attachments`,
          fd,
          { headers: { ...headers(), 'Content-Type': 'multipart/form-data' } }
        );
        next.push(res.data);
        toast.success(`${f.name} uploaded`);
      } catch (err) {
        toast.error(err.response?.data?.detail || `Failed to upload ${f.name}`);
      }
    }
    onChange(next);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = '';
  };

  const removeAttachment = async (att) => {
    if (!window.confirm(`Remove "${att.filename}"?`)) return;
    try {
      await axios.delete(
        `${API}/projects/${projectId}/procurement/attachments/${att.id}`,
        { headers: headers() }
      );
      onChange(items.filter((x) => x.id !== att.id));
      toast.success('Attachment removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not remove attachment');
    }
  };

  const downloadAttachment = async (att) => {
    try {
      const res = await axios.get(
        `${API}/projects/${projectId}/procurement/attachments/${att.id}/download`,
        { headers: headers(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', att.filename || 'download');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Download failed');
    }
  };

  return (
    <div className="col-span-2 space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-600 flex items-center gap-1">
          <Paperclip className="h-3.5 w-3.5" /> Attachments
        </span>
        {!isView && (
          <>
            <input
              ref={inputRef}
              type="file"
              multiple
              hidden
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="attachments-file-input"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="h-7 text-xs"
              data-testid="attachments-upload-btn"
            >
              {uploading ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Paperclip className="h-3 w-3 mr-1" />}
              {uploading ? 'Uploading…' : 'Upload files'}
            </Button>
          </>
        )}
      </div>

      {items.length === 0 ? (
        <div className="text-xs text-gray-500 italic px-2 py-3 border rounded-md border-dashed">
          No attachments yet.
        </div>
      ) : (
        <ul className="space-y-1.5">
          {items.map((att) => (
            <li
              key={att.id}
              className="flex items-center justify-between gap-2 border rounded-md px-2 py-1.5 bg-white hover:bg-gray-50"
              data-testid={`attachment-${att.id}`}
            >
              <div className="flex items-center gap-2 min-w-0">
                {iconFor(att.filename, att.content_type)}
                <div className="min-w-0">
                  <p className="text-sm text-gray-900 truncate">{att.filename}</p>
                  <p className="text-xs text-gray-500">
                    {formatSize(att.size)} · uploaded by {att.uploaded_by_name || '—'}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 flex-shrink-0">
                <button
                  type="button"
                  onClick={() => downloadAttachment(att)}
                  className="p-1 rounded hover:bg-gray-200"
                  title="Download"
                  data-testid={`attachment-download-${att.id}`}
                >
                  <Download className="h-3.5 w-3.5 text-gray-600" />
                </button>
                {!isView && (
                  <button
                    type="button"
                    onClick={() => removeAttachment(att)}
                    className="p-1 rounded hover:bg-red-50"
                    title="Remove"
                    data-testid={`attachment-remove-${att.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5 text-red-600" />
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default AttachmentsPicker;
